/**
 * Header Cart Count Badge Component
 * Updates ALL cart count elements across the page
 */

(function() {
  'use strict';

  /**
   * Updates ALL cart count indicators across the entire page
   * @param {number} itemCount - The number of items in the cart
   */
  function updateAllCartBadges(itemCount) {
    // 1. Update custom header cart badge
    const headerCartBadge = document.querySelector('.cart-count-badge');
    if (headerCartBadge) {
      headerCartBadge.textContent = String(itemCount);
      headerCartBadge.setAttribute('data-cart-count', itemCount);
      
      if (itemCount > 0) {
        headerCartBadge.classList.add('cart-count-badge--active');
      } else {
        headerCartBadge.classList.remove('cart-count-badge--active');
      }
    }

    // 2. Update ALL cart bubble elements (Shopify's built-in)
    const cartBubbles = document.querySelectorAll('[ref="cartBubbleCount"], .cart-bubble__text-count');
    cartBubbles.forEach(bubble => {
      bubble.textContent = itemCount < 100 ? String(itemCount) : '99+';
      bubble.classList.toggle('hidden', itemCount === 0);
    });

    // 3. Update cart bubble visibility
    const cartBubbleWrappers = document.querySelectorAll('[ref="cartBubble"], .cart-bubble');
    cartBubbleWrappers.forEach(wrapper => {
      wrapper.classList.toggle('visually-hidden', itemCount === 0);
      if (itemCount <= 99) {
        wrapper.setAttribute('data-maintain-ratio', '');
      } else {
        wrapper.removeAttribute('data-maintain-ratio');
      }
    });

    // 4. Update hidden cart item count elements
    const cartItemCountElements = document.querySelectorAll('[ref="cartItemCount"]');
    cartItemCountElements.forEach(el => {
      el.textContent = String(itemCount);
    });

    // 5. Update cart drawer heading (preserve the bubble)
    const drawerHeading = document.querySelector('#cart-drawer-heading');
    if (drawerHeading) {
      // Get the cart bubble HTML to preserve it
      const bubbleHtml = drawerHeading.querySelector('.cart-bubble');
      const titleText = itemCount > 0 ? 'Cart' : 'Your Cart is Empty';
      
      // Update only the text node, keep the bubble
      const textNode = Array.from(drawerHeading.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
      if (textNode) {
        textNode.textContent = titleText;
      } else {
        drawerHeading.childNodes[0].textContent = titleText;
      }
    }

    // 6. Update cart icons
    const cartIcons = document.querySelectorAll('cart-icon, .header-actions__cart-icon');
    cartIcons.forEach(icon => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    });

    // 7. Cart nav item in the desktop header: only shown when the cart has items.
    //    An empty cart has nothing to open, so the icon is dead weight in the bar.
    //    The Liquid renders it with an inline display:none when item_count is 0;
    //    this keeps that in sync after every client-side cart change.
    const cartNavItems = document.querySelectorAll('[data-cart-nav-item]');
    cartNavItems.forEach(item => {
      item.style.display = itemCount > 0 ? '' : 'none';
    });

    // 8. Mobile cart icon (next to the hamburger) follows the same rule
    const mobileCartIcons = document.querySelectorAll('[data-mobile-cart-icon]');
    mobileCartIcons.forEach(icon => {
      icon.style.display = itemCount > 0 ? '' : 'none';
    });
    const mobileCartCount = document.querySelector('[data-mobile-cart-count]');
    if (mobileCartCount) {
      mobileCartCount.textContent = itemCount < 100 ? String(itemCount) : '99+';
      mobileCartCount.setAttribute('data-mobile-cart-count', itemCount);
      mobileCartCount.classList.toggle('mobile-cart-count--active', itemCount > 0);
    }

    // 9. Dispatch custom event for other scripts
    document.dispatchEvent(new CustomEvent('cart:count-updated', {
      detail: { itemCount: itemCount },
      bubbles: true
    }));
  }

  /**
   * Format price in Shopify money format
   * @param {number} cents - Price in cents
   * @param {string} currency - Currency code
   */
  function formatMoney(cents, currency = 'USD') {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars} ${currency}`;
  }

  /**
   * Update cart total price display
   * @param {number} totalPrice - Total price in cents
   * @param {string} currency - Currency code
   */
  function updateCartTotal(totalPrice, currency) {
    const formattedPrice = formatMoney(totalPrice, currency);
    
    // Update all cart total elements
    const cartTotalElements = document.querySelectorAll(
      '[ref="cartTotal"], .cart__total-value, text-component[data-cart-subtotal]'
    );
    
    cartTotalElements.forEach(el => {
      el.textContent = formattedPrice;
      el.setAttribute('value', formattedPrice);
    });
    
    // Update caption (screen reader text)
    const cartCaption = document.querySelector('.cart-items__table caption span');
    if (cartCaption) {
      cartCaption.textContent = formattedPrice;
    }
  }

  /**
   * Fetch and update cart count AND total from Shopify API
   */
  function fetchAndUpdateCartCount() {
    return fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        updateAllCartBadges(cart.item_count);
        updateCartTotal(cart.total_price, cart.currency);
        return cart;
      })
      .catch(() => {
        // Silently fail - cart will update on next interaction
      });
  }

  /**
   * Aggressively watch for cart changes in the DOM
   */
  function watchCartChanges() {
    if (!document.body) return;

    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      let detectedCount = null;

      mutations.forEach(mutation => {
        // Check if cart items component changed
        if (mutation.target.matches && mutation.target.matches('cart-items-component, [class*="cart"]')) {
          shouldUpdate = true;
        }

        // Check for cart count elements in added nodes
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            const cartCountEl = node.querySelector ? node.querySelector('[ref="cartItemCount"]') : null;
            if (cartCountEl) {
              detectedCount = parseInt(cartCountEl.textContent, 10) || 0;
              shouldUpdate = true;
            }
          }
        });

        // Check for changes in cart bubble text
        if (mutation.type === 'characterData' || mutation.type === 'childList') {
          const target = mutation.target.parentElement || mutation.target;
          if (target.matches && target.matches('[ref="cartBubbleCount"], [ref="cartItemCount"]')) {
            detectedCount = parseInt(target.textContent, 10) || 0;
            shouldUpdate = true;
          }
        }
      });

      if (shouldUpdate) {
        if (detectedCount !== null) {
          updateAllCartBadges(detectedCount);
        } else {
          // Fetch from API if we don't have a count
          fetchAndUpdateCartCount();
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      characterDataOldValue: true
    });

    return observer;
  }

  /**
   * Initialize cart count listeners
   */
  function initCartCountListeners() {
    // 1. Listen for Shopify's theme events
    document.addEventListener('theme:cart:update', (event) => {
      const itemCount = event.detail?.data?.itemCount;
      if (itemCount !== undefined) {
        updateAllCartBadges(itemCount);
      } else {
        fetchAndUpdateCartCount();
      }
    });

    document.addEventListener('theme:cart:add', () => {
      setTimeout(fetchAndUpdateCartCount, 300);
    });

    // 2. Listen for Shopify section updates
    document.addEventListener('shopify:section:load', () => {
      setTimeout(fetchAndUpdateCartCount, 300);
    });

    // 3. Start watching DOM for changes
    watchCartChanges();

    // 4. Poll the cart API aggressively after any cart action
    let pollTimer = null;
    const startPolling = () => {
      if (pollTimer) clearInterval(pollTimer);
      
      let pollCount = 0;
      pollTimer = setInterval(() => {
        fetchAndUpdateCartCount();
        pollCount++;
        
        // Stop polling after 5 seconds (10 polls at 500ms intervals)
        if (pollCount >= 10) {
          clearInterval(pollTimer);
          pollTimer = null;
        }
      }, 500);
    };

    // Start polling on any cart interaction
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (target.closest('.cart-items__remove, .quantity-minus, .quantity-plus, [data-testid="cart-drawer-trigger"]')) {
        startPolling();
      }
    });

    // 5. Check cart count every 3 seconds (continuous sync)
    setInterval(() => {
      const cartCountEl = document.querySelector('[ref="cartItemCount"]');
      if (cartCountEl) {
        const actualCount = parseInt(cartCountEl.textContent, 10) || 0;
        const badgeEl = document.querySelector('.cart-count-badge');
        const bubbleEl = document.querySelector('[ref="cartBubbleCount"]');
        
        const currentBadgeCount = badgeEl ? (parseInt(badgeEl.textContent, 10) || 0) : -1;
        const currentBubbleCount = bubbleEl ? (parseInt(bubbleEl.textContent, 10) || 0) : -1;
        
        if (currentBadgeCount !== actualCount || currentBubbleCount !== actualCount) {
          updateAllCartBadges(actualCount);
        }
      }
    }, 3000);

    // 6. Handle page navigation
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        fetchAndUpdateCartCount();
      }
    });

    // 7. Initial sync on page load
    setTimeout(fetchAndUpdateCartCount, 500);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartCountListeners);
  } else {
    initCartCountListeners();
  }
})();
